import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Button } from 'Button';
import { Text } from 'Text';
import '../locales/streamDashboard/streamDashboard-en.json';

// getKeepLearningLesson picks the lesson. The button is the only control.

type Props = {
    lessonTitle: string;
    chapterNumber: number;
    lessonNumber: number;

    /*
        The button text, as one of the three states IsContentItem#launchText covered.

        These resolve against this module's own launch_* keys, not the contentItem.isContentItemMixin
        namespace they came from, which has no locale file loaded on the React side.
    */
    launchTextKey: 'review' | 'resume' | 'start';
    onLaunch: () => void;
    className?: string;
};

export const NextUpBox: FC<Props> = ({
    lessonTitle,
    chapterNumber,
    lessonNumber,
    launchTextKey,
    onLaunch,
    className,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <div
            className={twMerge(
                // Same chrome as the other main column cards.
                'flex flex-col items-stretch gap-2.5 rounded-8 border border-slate-grey-200 bg-white p-5',
                'text-center shadow-[0_2px_10px_0_rgba(0,0,0,0.1)]',
                /*
                    The 10px gap is the space between the text and the button when they stack, which is
                    the launch button's margin-top in the legacy rule. Side by side there is none: the
                    legacy button is absolutely positioned against the card's inset. This gap applies to
                    the card's two children only, so it never separates the two lines of text.
                */
                'sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:text-start',
                className,
            )}
        >
            {/* Centred on mobile and start-aligned from sm up, which is what
                `.lesson-index-box, .title { text-align: center }` and the sm override did.

                No gap between the two lines. The legacy title carries no margin or padding at either
                width, so the only separation is the line height. */}
            <div>
                <Text as="div" className="text-xs leading-5.5 text-black">
                    {t('chapter_number_x', { number: chapterNumber })}
                    <span className="mx-1">|</span>
                    {t('lesson_number_x', { number: lessonNumber })}
                </Text>
                <Text as="div" className="text-lg leading-5.5 text-black">
                    {/* NEXT UP: takes its own line on mobile and joins the title from sm up. It is the
                        only semibold text here, and it is black, not coral: the coral in the legacy
                        stylesheet is `.title, .next-up { color: $COLOR_V3_CORAL_DARK }`, which the
                        global `.grid-box .content-box` rule overrides to black. */}
                    <span className="block font-semibold sm:inline">{t('next_up')} </span>
                    <span className="uppercase">{lessonTitle}</span>
                </Text>
            </div>
            {/* Purple, not green. This card only shows once the course is under way, and the legacy
                rule was `.start-course-box button.inProgress { background-color: #9557EC }`. */}
            <Button color="purple" onPress={onLaunch} className="w-full shrink-0 sm:w-56">
                {t(`launch_${launchTextKey}`)}
            </Button>
        </div>
    );
};
